import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { loadModelViewer } from "../utils/loadModelViewer";

const LazyModelViewer = forwardRef(function LazyModelViewer(
  {
    wrapperClassName = "",
    style,
    placeholder = null,
    rootMargin = "240px 0px",
    ...modelProps
  },
  forwardedRef
) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isReady, setIsReady] = useState(() =>
    typeof window !== "undefined" && !!window.customElements?.get("model-viewer")
  );

  const fallbackHeight = useMemo(() => {
    if (style?.height) return style.height;
    return "300px";
  }, [style]);

  useEffect(() => {
    if (isReady) return;
    if (typeof window === "undefined") return;
    if (!containerRef.current) return;

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isReady, rootMargin]);

  useEffect(() => {
    if (!shouldLoad || isReady) return;
    loadModelViewer()
      .then(() => setIsReady(true))
      .catch(() => {});
  }, [shouldLoad, isReady]);

  const triggerLoad = () => {
    if (!shouldLoad) setShouldLoad(true);
  };

  return (
    <div
      ref={containerRef}
      className={wrapperClassName}
      onPointerEnter={triggerLoad}
      onTouchStart={triggerLoad}
      onFocus={triggerLoad}
    >
      {isReady ? (
        <model-viewer ref={forwardedRef} {...modelProps} style={style} />
      ) : (
        placeholder || (
          <div
            style={{
              width: style?.width || "100%",
              height: fallbackHeight,
              backgroundColor: "#f2f2f2",
            }}
            aria-hidden="true"
          />
        )
      )}
    </div>
  );
});

export default LazyModelViewer;
